import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, ShieldOff, UserPlus, KeyRound } from 'lucide-react'
import Drawer from './Drawer'
import { useConfirm } from './ConfirmDialog'
import ViewState from './ViewState'
import { useAuth } from '../contexts/AuthContext'
import { apiError } from '../lib/api'
import { fmtRelative } from '../i18n/format'

// Gestão de acesso ao painel. Não confundir com a aba Equipe, que são os OPERADORES do
// VMC (login por cartão RFID na cabine): aqui são as pessoas que entram no dashboard.
//
// Toda ação desta tela exige perfil admin no backend (routes.go). O gate visual é para
// não oferecer o que só devolveria 403; quem recusa de verdade é o servidor.

const ROLES = ['product', 'engineer', 'developer', 'admin']

const ROLE_CLS = {
  product: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
  engineer: 'bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300',
  developer: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  admin: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
}

const EMPTY = { email: '', name: '', role: 'product', password: '' }

export default function AdminIAM() {
  const { t } = useTranslation()
  const { user: me } = useAuth()
  const [users, setUsers] = useState(null)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [notice, setNotice] = useState(null)
  const [confirmDialog, ask] = useConfirm()

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await fetch('/api/users')
      if (!res.ok) throw await apiError(res)
      setUsers(await res.json())
    } catch (err) {
      setError(err.message)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const activeAdmins = (users || []).filter(u => u.role === 'admin' && u.status === 'active').length

  const changeRole = async (u, role) => {
    if (role === u.role) return
    setBusyId(u.id); setNotice(null)
    try {
      const res = await fetch(`/api/users/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: u.name, role, status: u.status }),
      })
      if (!res.ok) throw await apiError(res)
      const body = await res.json().catch(() => ({}))
      // Trocar o papel derruba as sessões vivas da pessoa, senão ela seguiria com o
      // papel antigo até o cookie vencer. Dizer quantas caíram evita a dúvida de "já
      // valeu?" logo depois de promover ou rebaixar alguém.
      if (body.sessionsRevoked > 0) {
        setNotice(t('admin:iam.sessions_revoked', { count: body.sessionsRevoked, email: u.email }))
      }
      await load()
    } catch (err) {
      setError(err.message)
    } finally { setBusyId(null) }
  }

  const toggleStatus = async (u) => {
    const status = u.status === 'active' ? 'disabled' : 'active'
    setBusyId(u.id)
    try {
      const res = await fetch(`/api/users/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: u.name, role: u.role, status }),
      })
      if (!res.ok) throw await apiError(res)
      await load()
    } catch (err) { setError(err.message) } finally { setBusyId(null) }
  }

  const revokeSessions = (u) => ask({
    title: t('admin:iam.revoke_title'),
    message: t('admin:iam.revoke_confirm', { email: u.email }),
    confirmLabel: t('admin:iam.revoke'),
    onConfirm: async () => {
      const res = await fetch(`/api/users/${u.id}/revoke-sessions`, { method: 'POST' })
      if (!res.ok) throw await apiError(res)
      const body = await res.json().catch(() => ({}))
      setNotice(t('admin:iam.sessions_revoked', { count: body.sessionsRevoked || 0, email: u.email }))
    },
  })

  const remove = (u) => ask({
    title: t('admin:iam.delete_title'),
    message: t('admin:iam.delete_confirm', { email: u.email }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => {
      const res = await fetch(`/api/users/${u.id}`, { method: 'DELETE' })
      if (!res.ok) throw await apiError(res)
      await load()
    },
  })

  const save = async (e) => {
    e.preventDefault()
    setSaving(true); setFormError(null)
    try {
      const body = { email: form.email.trim(), name: form.name.trim(), role: form.role }
      // Senha é opcional: sem ela a conta nasce como convite e só se liga à identidade
      // Google no primeiro SSO. Com ela, vira conta local (acesso de emergência).
      if (form.password) body.password = form.password
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw await apiError(res)
      setOpen(false); setForm(EMPTY); await load()
    } catch (err) { setFormError(err.message) } finally { setSaving(false) }
  }

  return (
    <div className="h-full flex flex-col">
      {confirmDialog}

      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('admin:iam.title')}</h2>
          <p className="text-xs text-slate-400 dark:text-slate-500">{t('admin:iam.subtitle')}</p>
        </div>
        <button
          onClick={() => { setForm(EMPTY); setFormError(null); setOpen(true) }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand-700"
        >
          <Plus size={14} /> {t('admin:iam.invite')}
        </button>
      </div>

      {notice && (
        <div className="mx-6 mt-4 rounded-lg border border-brand-200 bg-brand-25 px-3 py-2 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-900/30 dark:text-brand-300">
          {notice}
        </div>
      )}

      <div className="flex-1 overflow-auto scrollbar-thin px-6 py-4">
        <ViewState
          loading={users === null && !error}
          error={error}
          onRetry={load}
          empty={users?.length === 0}
          emptyIcon={UserPlus}
          emptyMessage={t('admin:iam.empty')}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                <th className="pb-2 font-semibold">{t('admin:iam.col_user')}</th>
                <th className="pb-2 font-semibold">{t('admin:iam.col_origin')}</th>
                <th className="pb-2 font-semibold">{t('admin:iam.col_role')}</th>
                <th className="pb-2 font-semibold">{t('admin:iam.col_last_login')}</th>
                <th className="pb-2 font-semibold text-right">{t('admin:iam.col_actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {(users || []).map(u => {
                const isMe = me?.email === u.email
                // O último admin ativo não pode ser rebaixado nem desligado, senão
                // ninguém mais consegue gerir o acesso. O backend também recusa.
                const lastAdmin = u.role === 'admin' && u.status === 'active' && activeAdmins <= 1
                return (
                  <tr key={u.id} className={u.status !== 'active' ? 'opacity-50' : ''}>
                    <td className="py-3">
                      <div className="flex items-center gap-2.5">
                        {u.picture ? (
                          <img src={u.picture} alt="" referrerPolicy="no-referrer" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            {(u.name || u.email).slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-700 dark:text-slate-200">
                            {u.name || u.email}
                            {isMe && <span className="ml-1.5 text-[10px] text-slate-400">{t('admin:iam.you')}</span>}
                          </p>
                          <p className="truncate text-xs text-slate-400 dark:text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        {u.provider === 'local' ? t('admin:iam.origin_local') : t('admin:iam.origin_google')}
                      </span>
                      {u.status === 'invited' && (
                        <span className="ml-1.5 text-[10px] text-amber-600 dark:text-amber-400">{t('admin:iam.invited')}</span>
                      )}
                      {u.status === 'disabled' && (
                        <span className="ml-1.5 text-[10px] text-red-600 dark:text-red-400">{t('admin:iam.disabled')}</span>
                      )}
                    </td>
                    <td className="py-3">
                      <select
                        value={u.role}
                        disabled={busyId === u.id || lastAdmin}
                        title={lastAdmin ? t('admin:iam.last_admin') : undefined}
                        onChange={e => changeRole(u, e.target.value)}
                        className={`rounded-md border-0 px-2 py-1 text-xs font-semibold disabled:cursor-not-allowed ${ROLE_CLS[u.role] || ''}`}
                      >
                        {ROLES.map(r => (
                          <option key={r} value={r}>{t(`auth:roles.${r}`)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 text-xs text-slate-400 dark:text-slate-500">
                      {u.lastLoginAt ? fmtRelative(u.lastLoginAt) : t('admin:iam.never')}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => revokeSessions(u)}
                          title={t('admin:iam.revoke')}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                        >
                          <KeyRound size={15} />
                        </button>
                        <button
                          onClick={() => toggleStatus(u)}
                          disabled={busyId === u.id || lastAdmin}
                          title={u.status === 'active' ? t('admin:iam.disable') : t('admin:iam.enable')}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40 dark:hover:bg-slate-800"
                        >
                          <ShieldOff size={15} />
                        </button>
                        <button
                          onClick={() => remove(u)}
                          disabled={lastAdmin}
                          title={t('common:actions.delete')}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-40 dark:hover:bg-red-900/30"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </ViewState>
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title={t('admin:iam.invite_title')}>
        <form onSubmit={save} className="space-y-4 p-5">
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('admin:iam.invite_help')}</p>
          <Field label={t('auth:email')}>
            <input
              type="email" required value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
          </Field>
          <Field label={t('admin:iam.name')}>
            <input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
          </Field>
          <Field label={t('admin:iam.col_role')}>
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
            >
              {ROLES.map(r => <option key={r} value={r}>{t(`auth:roles.${r}`)}</option>)}
            </select>
          </Field>
          <Field label={t('admin:iam.password_optional')}>
            <input
              type="password" value={form.password} autoComplete="new-password"
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
            <p className="mt-1 text-[11px] text-slate-400">{t('admin:iam.password_help')}</p>
          </Field>
          {formError && <p className="text-xs text-red-500">{formError}</p>}
          <button
            type="submit" disabled={saving}
            className="w-full rounded-lg bg-brand py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {t('admin:iam.invite')}
          </button>
        </form>
      </Drawer>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-300">{label}</span>
      {children}
    </label>
  )
}
